/**
 * Modal dialogs on the native <dialog> element: Esc closes them (native
 * `cancel`), a click on the backdrop or a [data-close-dialog] button closes
 * them, and focus returns to the control that opened them. Opening a dialog
 * from inside another one replaces it and keeps the original return target.
 *
 * Markup: <button type="button" data-open-dialog="login-dialog">,
 *         <dialog id="login-dialog"> ... <button data-close-dialog>.
 */

const returnTargets = new WeakMap<HTMLDialogElement, HTMLElement>();

function openDialogElement(): HTMLDialogElement | null {
  const open = document.querySelector("dialog[open]");
  return open instanceof HTMLDialogElement ? open : null;
}

/** Open `dialog` as a modal; focus goes back to the current focus on close. */
export function openDialog(dialog: HTMLDialogElement): void {
  if (dialog.open) return;
  const current = openDialogElement();
  const focused = document.activeElement;
  const returnTo =
    current === null
      ? focused instanceof HTMLElement
        ? focused
        : null
      : (returnTargets.get(current) ?? null);
  if (current !== null) {
    returnTargets.delete(current);
    current.close();
  }
  if (returnTo !== null) returnTargets.set(dialog, returnTo);
  dialog.showModal();
}

export function closeDialog(dialog: HTMLDialogElement): void {
  if (dialog.open) dialog.close();
}

export function dialogById(id: string): HTMLDialogElement | null {
  const element = document.getElementById(id);
  return element instanceof HTMLDialogElement ? element : null;
}

/**
 * The `close` event comes asynchronously, after browsers may already have
 * returned the focus themselves and the visitor may have moved on: only an
 * actually lost focus (on <body> or still inside the closed dialog) is moved.
 */
function restoreFocus(event: Event): void {
  const dialog = event.target;
  if (!(dialog instanceof HTMLDialogElement)) return;
  const target = returnTargets.get(dialog);
  returnTargets.delete(dialog);
  const active = document.activeElement;
  const focusLost =
    active === null || active === document.body || dialog.contains(active);
  if (
    target?.isConnected === true &&
    openDialogElement() === null &&
    focusLost
  ) {
    target.focus();
  }
}

function onClick(event: MouseEvent): void {
  const target = event.target;
  if (target instanceof HTMLDialogElement) {
    // a click on the backdrop lands on the dialog element itself
    const box = target.getBoundingClientRect();
    const inside =
      event.clientX >= box.left &&
      event.clientX <= box.right &&
      event.clientY >= box.top &&
      event.clientY <= box.bottom;
    if (!inside) target.close();
    return;
  }
  if (!(target instanceof Element)) return;
  const opener = target.closest("[data-open-dialog]");
  if (opener instanceof HTMLElement) {
    const dialog = dialogById(opener.dataset["openDialog"] ?? "");
    if (dialog !== null) {
      event.preventDefault();
      openDialog(dialog);
    }
    return;
  }
  const closer = target.closest("[data-close-dialog]");
  const dialog = closer?.closest("dialog");
  if (dialog instanceof HTMLDialogElement) dialog.close();
}

/** Wire every dialog of the page (once, at start-up). */
export function initDialogs(): void {
  document.addEventListener("click", onClick);
  for (const dialog of document.querySelectorAll("dialog")) {
    dialog.addEventListener("close", restoreFocus);
  }
}
